import { Ref, useEffect, useRef, useState } from 'react';

export type CarouselProps = {
  containerRef: Ref<HTMLUListElement>;
  firstItemRef: Ref<HTMLLIElement>;
  lastItemRef: Ref<HTMLLIElement>;
  next: () => void;
  nextDisabled: boolean;
  prev: () => void;
  prevDisabled: boolean;
};

function useEdgeInView(root: Element | null): [Ref<any>, boolean] {
  const elementRef = useRef<Element | null>(null);
  const [inView, setInView] = useState(false);

  const ref = (node: Element | null) => {
    elementRef.current = node;
  };

  useEffect(() => {
    const node = elementRef.current;

    if (!node) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { root, threshold: 1 }
    );

    observer.observe(node);

    return () => observer.disconnect();
  });

  return [ref, inView];
}

export default function useCarousel(): CarouselProps {
  const containerRef = useRef<HTMLUListElement>(null);

  const [carousel, setCarousel] = useState<any>();
  const [itemWidth, setItemWidth] = useState(0);
  const [itemsPerSlide, setItemsPerSlide] = useState(0);

  const [firstItemRef, prevDisabled] = useEdgeInView(containerRef.current);
  const [lastItemRef, nextDisabled] = useEdgeInView(containerRef.current);

  const next = () => carousel && carousel.scrollBy({ left: itemWidth * itemsPerSlide, top: 0, behavior: 'smooth' });
  const prev = () => carousel && carousel.scrollBy({ left: -itemWidth * itemsPerSlide, top: 0, behavior: 'smooth' });

  useEffect(() => {
    if (containerRef && containerRef.current) {
      const carousel = containerRef.current;
      const itemWidth = (carousel.firstElementChild as HTMLElement | null)?.clientWidth || 0;
      const itemsPerSlide = itemWidth ? Math.floor(carousel.clientWidth / itemWidth) : 0;

      setCarousel(carousel);
      setItemWidth(itemWidth);
      setItemsPerSlide(itemsPerSlide);
    }
  }, []);

  return {
    containerRef,
    firstItemRef: firstItemRef as Ref<HTMLLIElement>,
    lastItemRef: lastItemRef as Ref<HTMLLIElement>,
    next,
    prev,
    prevDisabled,
    nextDisabled,
  };
}
